import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import Select from 'react-select';
import DatasetSelector from '../../Common/DatasetSelector';
import DiscretizationLayout from '../../Common/DiscretizationLayout';

const LineChart = ({ plotData, defaultTitle, xlabel, ylabel, titleKey, showMrpLine = false }) => {
  const plotRef = useRef(null);
  const [title, setTitle] = useState(() => localStorage.getItem(`mrp_numbers_title_${titleKey}`) || defaultTitle || '');
  const [xLabel, setXLabel] = useState(() => localStorage.getItem(`mrp_numbers_xlabel_${titleKey}`) || xlabel || '');
  const [yLabel, setYLabel] = useState(() => localStorage.getItem(`mrp_numbers_ylabel_${titleKey}`) || ylabel || '');
  const [showEditor, setShowEditor] = useState(false);
  const defaultColors = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'];

  const renderPlot = (t, xl, yl) => {
    if (!plotData || !plotRef.current || !window.Plotly) return;
    const traces = [];
    plotData.forEach((series, idx) => {
      const color = defaultColors[idx % defaultColors.length];
      const groupName = series.name;
      traces.push({
        x: series.x,
        y: series.y,
        type: 'scatter',
        mode: 'lines+markers',
        name: series.name,
        legendgroup: groupName,
        line: { width: 2, color: color },
        marker: { size: 6 }
      });
      if (showMrpLine && series.mrp !== undefined && series.mrp !== null) {
        traces.push({
          x: [series.mrp, series.mrp],
          y: [0, Math.max(...series.y)],
          type: 'scatter',
          mode: 'lines',
          name: `${series.name} MRP`,
          legendgroup: groupName,
          showlegend: false,
          line: { dash: 'dash', width: 2, color: color }
        });
      }
    });
    window.Plotly.newPlot(plotRef.current, traces, {
      title: t,
      xaxis: { title: xl },
      yaxis: { title: yl },
      template: 'plotly_white',
      height: 400,
      legend: { orientation: 'h', y: -0.2 },
      margin: { t: 40, b: 80, l: 60, r: 20 }
    }, { responsive: true });
  };

  useEffect(() => { if (plotData) renderPlot(title, xLabel, yLabel); }, [plotData, title, xLabel, yLabel]);

  const applyTitles = () => {
    renderPlot(title, xLabel, yLabel);
    localStorage.setItem(`mrp_numbers_title_${titleKey}`, title);
    localStorage.setItem(`mrp_numbers_xlabel_${titleKey}`, xLabel);
    localStorage.setItem(`mrp_numbers_ylabel_${titleKey}`, yLabel);
    setShowEditor(false);
  };

  if (!plotData) return null;
  return (
    <div style={{ marginBottom: '20px', background: 'white', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>{title}</h4>
        <button onClick={() => setShowEditor(!showEditor)} style={{ padding: '2px 8px', fontSize: '11px', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>✏️ Edit</button>
      </div>
      {showEditor && (
        <div style={{ display: 'flex', gap: '8px', padding: '8px', background: '#f9f9f9', borderRadius: '4px', marginBottom: '8px', flexWrap: 'wrap' }}>
          <input placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} style={{ flex: 2, fontSize: '12px', padding: '4px' }} />
          <input placeholder="X axis" value={xLabel} onChange={e => setXLabel(e.target.value)} style={{ flex: 1, fontSize: '12px', padding: '4px' }} />
          <input placeholder="Y axis" value={yLabel} onChange={e => setYLabel(e.target.value)} style={{ flex: 1, fontSize: '12px', padding: '4px' }} />
          <button onClick={applyTitles} style={{ background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 12px', cursor: 'pointer' }}>Apply</button>
        </div>
      )}
      <div ref={plotRef} style={{ width: '100%', height: '400px' }}></div>
    </div>
  );
};

const MRPNumbers = () => {
  const [step, setStep] = useState(0);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [datasetInfo, setDatasetInfo] = useState(null);
  const [datasetLoaded, setDatasetLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('cumulative');
  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  
  const [groups, setGroups] = useState({
    facility: false,
    dates: false,
    segmentation: false,
    status: false,
    target: false
  });
  
  const [formData, setFormData] = useState({
    Facility_ID: null,
    Default_Date: null,
    Closed_Date: null,
    Frequency: { value: 'Monthly', label: 'Monthly' },
    Calibration_segment: null,
    Grade: null,
    Status: null,
    Threshold_MRP: 2
  });

  const frequencyOptions = [
    { value: 'Monthly', label: 'Monthly' },
    { value: 'Quarterly', label: 'Quarterly' },
    { value: 'Annual', label: 'Annual' }
  ];

  const columnOptions = columns.map(c => ({ value: c, label: c }));

  useEffect(() => {
    if (!window.Plotly) {
      const script = document.createElement('script');
      script.src = 'https://cdn.plot.ly/plotly-2.27.0.min.js';
      document.head.appendChild(script);
    }
  }, []);

  const hdl = (data) => {
    setColumns(data.columns || []);
    setDatasetInfo(data);
    setDatasetLoaded(true);
    toast.success(`Loaded ${data.rows?.toLocaleString()} rows`);
  };

  const hfc = (f, v) => setFormData(p => ({ ...p, [f]: v }));

  const loadStatusValues = async () => {
    if (!formData.Status || !datasetInfo?.id) return;
    try {
      const res = await axios.get(`/api/cart/column-values?dataset_id=${datasetInfo.id}&column=${formData.Status.value}`);
      setStatusOptions(res.data.values.map(v => ({ value: v, label: String(v) })));
    } catch (err) { console.error(err); }
  };

  useEffect(() => { if (formData.Status) loadStatusValues(); }, [formData.Status, datasetInfo]);

  const toggleGroup = (group) => {
    setGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const saveParams = async () => {
    const required = ['Facility_ID', 'Default_Date', 'Closed_Date', 'Status'];
    for (let f of required) {
      if (!formData[f]) {
        toast.error(`Please select ${f}`);
        return;
      }
    }
    if (!formData.Grade && !formData.Calibration_segment) {
      toast.error('Please select either Grade or Calibration segment (or both)');
      return;
    }
    if (selectedStatus.length === 0) {
      toast.error('Please select at least one value for "Unclosed flag"');
      return;
    }
    const payload = {
      Facility_ID: formData.Facility_ID.value,
      Default_Date: formData.Default_Date.value,
      Closed_Date: formData.Closed_Date.value,
      Frequency: formData.Frequency.value,
      Calibration_segment: formData.Calibration_segment?.value || '',
      Grade: formData.Grade?.value || '',
      Status: formData.Status.value,
      Threshold_MRP: formData.Threshold_MRP,
      Status_values: selectedStatus.map(s => s.value)
    };
    setLoading(true);
    try {
      await axios.post('/api/mrp-numbers/save-params', payload);
      toast.success('Parameters saved');
      setStep(1);
    } catch (err) {
      toast.error('Failed to save parameters');
    } finally { setLoading(false); }
  };

  const runAnalysis = async () => {
    const payload = {
      Facility_ID: formData.Facility_ID.value,
      Default_Date: formData.Default_Date.value,
      Closed_Date: formData.Closed_Date.value,
      Frequency: formData.Frequency.value,
      Calibration_segment: formData.Calibration_segment?.value || '',
      Grade: formData.Grade?.value || '',
      Status: formData.Status.value,
      Threshold_MRP: formData.Threshold_MRP,
      Status_values: selectedStatus.map(s => s.value)
    };
    setLoading(true);
    try {
      const response = await axios.post('/api/mrp-numbers/run-analysis', payload);
      setResults(response.data.results);
      toast.success(response.data.message);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally { setLoading(false); }
  };

  
  const saveMRPColumn = async () => {
    const colName = prompt('Enter new column name for MRP:', 'mrp_value');
    if (!colName) return;
    setLoading(true);
    try {
      await axios.post('/api/mrp-numbers/save-mrp', { new_column_name: colName });
      toast.success(`MRP saved as '${colName}'`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save MRP');
    } finally {
      setLoading(false);
    }
  };


  const exportResults = async () => {
    const customTitles = {
      cumulative_title: localStorage.getItem('mrp_numbers_title_cumulative') || '',
      cumulative_xlabel: localStorage.getItem('mrp_numbers_xlabel_cumulative') || 'Time in Default',
      cumulative_ylabel: localStorage.getItem('mrp_numbers_ylabel_cumulative') || 'Cumulative Number of Defaults',
      mrp_title: localStorage.getItem('mrp_numbers_title_mrp') || '',
      mrp_xlabel: localStorage.getItem('mrp_numbers_xlabel_mrp') || 'Time in Default',
      mrp_ylabel: localStorage.getItem('mrp_numbers_ylabel_mrp') || '% Cumulative Defaults'
    };
    try {
      const response = await axios.post('/api/mrp-numbers/export-with-titles', { custom_titles: customTitles }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mrp_numbers_results.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error('Failed to export results'); }
  };

  const sc = (t) => {
    if (t === 0) setStep(0);
    else if (t === 1 && datasetLoaded && formData.Facility_ID) setStep(1);
    else if (t === 2 && results) setStep(2);
  };

  const cs = {
    control: (b) => ({ ...b, minHeight: '36px', fontSize: '13px', borderColor: '#e0e0e0' }),
    valueContainer: (b) => ({ ...b, padding: '0 8px' }),
    placeholder: (b) => ({ ...b, fontSize: '13px', color: '#95a5a6' }),
    menu: (b) => ({ ...b, zIndex: 9999, position: 'absolute' }),
    menuPortal: (b) => ({ ...b, zIndex: 9999 })
  };

  const cumulativeCurves = results?.interactive?.avg_curves?.map(curve => ({ name: curve.grade, x: curve.x, y: curve.y })) || [];
  const mrpCurves = results?.interactive?.pct_curves?.map(curve => ({ name: curve.grade, x: curve.x, y: curve.y, mrp: curve.mrp })) || [];

  const groupStyle = {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    marginBottom: '16px',
    overflow: 'visible',
    position: 'relative'
  };
  
  const groupHeaderStyle = {
    background: '#f5f7fa',
    padding: '12px 16px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: '14px',
    color: '#2c3e50'
  };
  
  const groupContentStyle = {
    padding: '16px',
    background: 'white',
    overflow: 'visible'
  };

  return (
    <DiscretizationLayout title="Max Recovery Period - Numbers" step={step} onStepClick={sc} datasetLoaded={datasetLoaded} results={results}>
      {step === 0 && (
        <>
          <DatasetSelector apiPrefix="/api/mrp-numbers" onDatasetLoaded={hdl} />
          {columns.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              {/* 1. Facility identifier */}
              <div style={groupStyle}>
                <div style={groupHeaderStyle} onClick={() => toggleGroup('facility')}>
                  <span>🏢 1. Facility identifier</span>
                  <span>{groups.facility ? '−' : '+'}</span>
                </div>
                {groups.facility && (
                  <div style={groupContentStyle}>
                    <div style={{ maxWidth: '400px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Facility ID *</label>
                      <Select options={columnOptions} value={formData.Facility_ID} onChange={v => hfc('Facility_ID', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select facility ID column..." />
                    </div>
                  </div>
                )}
              </div>

              {/* 2. Key dates */}
              <div style={groupStyle}>
                <div style={groupHeaderStyle} onClick={() => toggleGroup('dates')}>
                  <span>📅 2. Key dates</span>
                  <span>{groups.dates ? '−' : '+'}</span>
                </div>
                {groups.dates && (
                  <div style={groupContentStyle}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Default Date *</label>
                        <Select options={columnOptions} value={formData.Default_Date} onChange={v => hfc('Default_Date', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select default date column..." />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Closed Date *</label>
                        <Select options={columnOptions} value={formData.Closed_Date} onChange={v => hfc('Closed_Date', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select closed date column..." />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Frequency *</label>
                        <Select options={frequencyOptions} value={formData.Frequency} onChange={v => hfc('Frequency', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select frequency..." />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 3. Segmentation */}
              <div style={groupStyle}>
                <div style={groupHeaderStyle} onClick={() => toggleGroup('segmentation')}>
                  <span>🎯 3. Segmentation</span>
                  <span>{groups.segmentation ? '−' : '+'}</span>
                </div>
                {groups.segmentation && (
                  <div style={groupContentStyle}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Calibration segment (optional)</label>
                        <Select options={columnOptions} value={formData.Calibration_segment} onChange={v => hfc('Calibration_segment', v)} isClearable styles={cs} menuPortalTarget={document.body} placeholder="Select calibration segment..." />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Grade (optional)</label>
                        <Select options={columnOptions} value={formData.Grade} onChange={v => hfc('Grade', v)} isClearable styles={cs} menuPortalTarget={document.body} placeholder="Select grade column..." />
                      </div>
                    </div>
                    <p style={{ fontSize: '11px', color: '#e74c3c', marginTop: '8px' }}>⚠️ At least one of Grade or Calibration segment must be selected</p>
                  </div>
                )}
              </div>

              {/* 4. Default status */}
              <div style={groupStyle}>
                <div style={groupHeaderStyle} onClick={() => toggleGroup('status')}>
                  <span>⚠️ 4. Default status</span>
                  <span>{groups.status ? '−' : '+'}</span>
                </div>
                {groups.status && (
                  <div style={groupContentStyle}>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Status *</label>
                      <Select options={columnOptions} value={formData.Status} onChange={v => hfc('Status', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select status column..." />
                    </div>
                    {formData.Status && statusOptions.length > 0 && (
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Unclosed flag * (select values to include)</label>
                        <Select options={statusOptions} isMulti value={selectedStatus} onChange={setSelectedStatus} styles={cs} menuPortalTarget={document.body} placeholder="Select status values..." />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 5. Target variable */}
              <div style={groupStyle}>
                <div style={groupHeaderStyle} onClick={() => toggleGroup('target')}>
                  <span>📊 5. Target variable</span>
                  <span>{groups.target ? '−' : '+'}</span>
                </div>
                {groups.target && (
                  <div style={groupContentStyle}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Threshold MRP (%)</label>
                      <input type="number" value={formData.Threshold_MRP} onChange={e => hfc('Threshold_MRP', parseInt(e.target.value))} style={{ width: '200px', padding: '8px', fontSize: '13px', border: '1px solid #e0e0e0', borderRadius: '4px' }} />
                      <p style={{ fontSize: '10px', color: '#7f8c8d', marginTop: '4px' }}>Percentage threshold for MRP calculation (default: 2%)</p>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button onClick={saveParams} disabled={loading} style={{ padding: '8px 28px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>{loading ? "Saving..." : "Continue →"}</button>
              </div>
            </div>
          )}
        </>
      )}

      {step === 1 && (
        <div>
          <div style={{ background: '#f9f9f9', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600 }}>Review Parameters</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '8px', fontSize: '13px' }}>
              <p><strong>Facility ID:</strong> {formData.Facility_ID?.label}</p>
              <p><strong>Default Date:</strong> {formData.Default_Date?.label}</p>
              <p><strong>Closed Date:</strong> {formData.Closed_Date?.label}</p>
              <p><strong>Frequency:</strong> {formData.Frequency?.label}</p>
              <p><strong>Grade:</strong> {formData.Grade?.label || "(not provided)"}</p>
              <p><strong>Status:</strong> {formData.Status?.label}</p>
              <p><strong>Threshold MRP:</strong> {formData.Threshold_MRP}%</p>
              {formData.Calibration_segment && <p><strong>Calibration segment:</strong> {formData.Calibration_segment.label}</p>}
              <p><strong>Unclosed flag filter:</strong> {selectedStatus.map(s => s.label).join(', ') || 'None'}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={() => setStep(0)} style={{ padding: '8px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>← Back</button>
            <button onClick={runAnalysis} disabled={loading} style={{ padding: '8px 24px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>{loading ? "Running..." : "Run Analysis →"}</button>
          </div>
        </div>
      )}

      {step === 2 && results && (
        <div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '1px solid #e0e0e0' }}>
            <button onClick={() => setActiveTab('cumulative')} style={{ padding: '8px 16px', fontSize: '13px', background: activeTab === 'cumulative' ? '#1abc9c' : 'transparent', color: activeTab === 'cumulative' ? 'white' : '#2c3e50', border: 'none', borderBottom: activeTab === 'cumulative' ? '2px solid #1abc9c' : 'none', cursor: 'pointer' }}>Cumulative Defaults</button>
            <button onClick={() => setActiveTab('mrp')} style={{ padding: '8px 16px', fontSize: '13px', background: activeTab === 'mrp' ? '#1abc9c' : 'transparent', color: activeTab === 'mrp' ? 'white' : '#2c3e50', border: 'none', borderBottom: activeTab === 'mrp' ? '2px solid #1abc9c' : 'none', cursor: 'pointer' }}>MRP Curve</button>
            <button onClick={() => setActiveTab('tables')} style={{ padding: '8px 16px', fontSize: '13px', background: activeTab === 'tables' ? '#1abc9c' : 'transparent', color: activeTab === 'tables' ? 'white' : '#2c3e50', border: 'none', borderBottom: activeTab === 'tables' ? '2px solid #1abc9c' : 'none', cursor: 'pointer' }}>Data Tables</button>
          </div>

          {activeTab === 'cumulative' && (
            <LineChart plotData={cumulativeCurves} defaultTitle={`Average Cumulative Defaults by Grade (${results.meta?.Frequency || 'Monthly'})`} xlabel={`Time in Default (${results.meta?.Frequency || 'Monthly'})`} ylabel="Cumulative Number of Defaults" titleKey="cumulative" showMrpLine={false} />
          )}

          {activeTab === 'mrp' && (
            <LineChart plotData={mrpCurves} defaultTitle={`Cumulative % Defaults with MRP (${results.meta?.Frequency || 'Monthly'})`} xlabel={`Time in Default (${results.meta?.Frequency || 'Monthly'})`} ylabel="% Cumulative Defaults" titleKey="mrp" showMrpLine={true} />
          )}

          {activeTab === 'tables' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div>
                <h4 style={{ fontSize: '14px', marginBottom: '8px' }}>Average Cumulative Defaults</h4>
                <div className="table-scroll" style={{ maxHeight: '250px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                  <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#34495e', color: 'white' }}>
                      <tr>
                        {results.tables?.avg_cumulated?.length > 0 && Object.keys(results.tables.avg_cumulated[0]).map(k => (
                          <th key={k} style={{ padding: '8px' }}>{k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {results.tables?.avg_cumulated?.map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                          {Object.values(row).map((val, j) => (
                            <td key={j} style={{ padding: '6px' }}>{typeof val === 'number' ? val.toFixed(4) : val}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <h4 style={{ fontSize: '14px', marginBottom: '8px' }}>Percentage Cumulative Defaults (MRP)</h4>
                <div className="table-scroll" style={{ maxHeight: '250px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                  <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#34495e', color: 'white' }}>
                      <tr>
                        {results.tables?.pct_cumulative?.length > 0 && Object.keys(results.tables.pct_cumulative[0]).map(k => (
                          <th key={k} style={{ padding: '8px' }}>{k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {results.tables?.pct_cumulative?.map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                          {Object.values(row).map((val, j) => (
                            <td key={j} style={{ padding: '6px' }}>{typeof val === 'number' ? val.toFixed(4) : val}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <h4 style={{ fontSize: '14px', marginBottom: '8px' }}>MRP Values</h4>
                <div className="table-scroll" style={{ maxHeight: '250px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                  <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#34495e', color: 'white' }}>
                      <tr>
                        {results.tables?.mrp?.length > 0 && Object.keys(results.tables.mrp[0]).map(k => (
                          <th key={k} style={{ padding: '8px' }}>{k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {results.tables?.mrp?.map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                          {Object.values(row).map((val, j) => (
                            <td key={j} style={{ padding: '6px' }}>{val}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button onClick={() => setStep(1)} style={{ padding: '8px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>← Back</button>
            <button onClick={exportResults} style={{ padding: '8px 20px', background: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>📥 Export</button>
            <button onClick={saveMRPColumn} style={{ padding: '8px 20px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>{loading ? "Saving..." : "💾 Save MRP to Dataset"}</button>
          </div>
        </div>
      )}
    </DiscretizationLayout>
  );
};

export default MRPNumbers;
